import type { McIndividualSchedulePublic, ScheduleFilters } from '../../types/mc-schedules'

export interface PublicSchedulesResponse {
  success: boolean
  data: McIndividualSchedulePublic[]
  pagination?: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export async function getPublicMcIndividualSchedules(
  filters?: ScheduleFilters
): Promise<{ data: McIndividualSchedulePublic[] | null; error: { message: string } | null }> {
  try {
    const query = new URLSearchParams()
    
    if (filters?.location && filters.location !== 'all') {
      query.append('location', filters.location)
    }
    
    if (filters?.category && filters.category !== 'all') {
      query.append('category', filters.category)
    }
    
    if (filters?.subCategory && filters.subCategory !== 'all') {
      query.append('subCategory', filters.subCategory)
    }
    
    if (filters?.startDate) {
      query.append('startDate', filters.startDate)
    }
    
    if (filters?.endDate) {
      query.append('endDate', filters.endDate)
    }

    const queryString = query.toString()
    const url = `/api/mc-individual-schedules/public-list${queryString ? `?${queryString}` : ''}`
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      return {
        data: null,
        error: {
          message: errorData.error?.message || errorData.error || `HTTP ${response.status}`,
        },
      }
    }

    const data = await response.json() as PublicSchedulesResponse
    
    if (!data.success) {
      const err = data as unknown as { success: boolean; error?: { message: string } }
      return {
        data: null,
        error: {
          message: err.error?.message || 'Invalid response from server',
        },
      }
    }

    return {
      data: data.data || [],
      error: null,
    }
  } catch (error) {
    return {
      data: null,
      error: {
        message: error instanceof Error ? error.message : 'Failed to fetch schedules',
      },
    }
  }
}

export async function getPublicMcIndividualScheduleById(
  id: string
): Promise<{ data: McIndividualSchedulePublic | null; error: { message: string } | null }> {
  try {
    const response = await fetch(`/api/mc-individual-schedules/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      return {
        data: null,
        error: {
          message: errorData.error?.message || errorData.error || `HTTP ${response.status}`,
        },
      }
    }

    const data = await response.json() as { success: boolean; data: McIndividualSchedulePublic }
    
    if (!data.success) {
      const err = data as unknown as { success: boolean; error?: { message: string } }
      return {
        data: null,
        error: {
          message: err.error?.message || 'Schedule not found',
        },
      }
    }

    return {
      data: data.data,
      error: null,
    }
  } catch (error) {
    return {
      data: null,
      error: {
        message: error instanceof Error ? error.message : 'Failed to fetch schedule',
      },
    }
  }
}
